/**
 * Pull-to-refresh, for the installed app only.
 *
 * Safari draws its own reload button and its own pull gesture; an app added
 * to the home screen has neither, so on iOS there is no way to reload it short
 * of killing it from the app switcher. A browser tab keeps its own gesture,
 * which is why this checks that it is installed before doing anything.
 *
 * A reload throws away whatever is typed and unsaved. A screen with unsaved
 * work marks itself with `data-unsaved` (on its save bar, which is on screen
 * exactly as long as there is something to lose), and a pull over it asks for
 * a save or a discard first instead of reloading.
 */

/** How far a finger has to pull, in CSS pixels, before letting go reloads. */
export const PULL_THRESHOLD = 96;

/** The indicator moves at half the finger's speed and stops here. */
export const PULL_MAX = 72;

export type PullOutcome = "none" | "refresh" | "blocked";

export function pullOutcome(distance: number, unsaved: boolean): PullOutcome {
  if (distance < PULL_THRESHOLD) return "none";
  return unsaved ? "blocked" : "refresh";
}

/** What the indicator shows for a pull this far: damped, and capped. */
export const pullOffset = (distance: number): number => Math.min(PULL_MAX, Math.max(0, distance) / 2);

/** True when running as an app added to the home screen, on iOS or anywhere else. */
export function isInstalled(): boolean {
  if (typeof window === "undefined") return false;
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios || (window.matchMedia?.("(display-mode: standalone)").matches ?? false);
}

/** Touches that start here are somebody using a control, not pulling the page. */
export const PULL_IGNORES =
  'input, textarea, select, [contenteditable], [role="dialog"], [role="menu"], .scan-view, .menu-scrim';

/** Something modal is open: the page under it is not what a pull would reload. */
export const PULL_BLOCKERS = '[aria-modal="true"], .menu-scrim';
